/**
 * Cognito Hosted UI OAuth (Authorization Code + PKCE) helpers.
 *
 * Flow:
 * 1. `startLogin()` generates a PKCE verifier (random) + S256 challenge,
 *    stashes the verifier in `sessionStorage`, and redirects to Cognito.
 * 2. Cognito redirects back to `/auth/callback?code=...`.
 * 3. `exchangeCode(code)` reads the verifier, POSTs to the token endpoint,
 *    clears the verifier, and returns the tokens.
 *
 * Tokens themselves never touch storage; the caller keeps them in memory
 * (see `AuthContext`).
 */

const VERIFIER_STORAGE_KEY = 'cognito.pkce.verifier';
const STATE_STORAGE_KEY = 'cognito.oauth.state';

export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number;
}

interface CognitoConfig {
  domain: string;
  clientId: string;
  redirectUri: string;
}

function readConfig(): CognitoConfig {
  const rawDomain = import.meta.env.VITE_COGNITO_DOMAIN;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  if (!rawDomain || !clientId) {
    throw new Error('Missing VITE_COGNITO_DOMAIN or VITE_COGNITO_CLIENT_ID');
  }
  // Accept both "host.example.com" and "https://host.example.com"; normalise.
  const domain = rawDomain.startsWith('http') ? rawDomain : `https://${rawDomain}`;
  const redirectUri = `${window.location.origin}/auth/callback`;
  return { domain, clientId, redirectUri };
}

async function sha256(input: string): Promise<ArrayBuffer> {
  return await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
}

function base64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64url(bytes.buffer);
}

/**
 * Redirect to Cognito Hosted UI to begin the auth code + PKCE flow.
 * Persists the PKCE verifier + opaque state in `sessionStorage`.
 */
export async function startLogin(): Promise<void> {
  const { domain, clientId, redirectUri } = readConfig();
  const verifier = randomVerifier();
  const challenge = base64url(await sha256(verifier));
  const state = randomVerifier();

  sessionStorage.setItem(VERIFIER_STORAGE_KEY, verifier);
  sessionStorage.setItem(STATE_STORAGE_KEY, state);

  const url = new URL(`${domain}/oauth2/authorize`);
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);

  window.location.assign(url.toString());
}

/**
 * Exchange an authorization code for tokens. Consumes the stored PKCE
 * verifier and state. Throws if either is missing or if the exchange fails.
 */
export async function exchangeCode(code: string, state: string): Promise<CognitoTokens> {
  const { domain, clientId, redirectUri } = readConfig();

  const verifier = sessionStorage.getItem(VERIFIER_STORAGE_KEY);
  const storedState = sessionStorage.getItem(STATE_STORAGE_KEY);
  sessionStorage.removeItem(VERIFIER_STORAGE_KEY);
  sessionStorage.removeItem(STATE_STORAGE_KEY);

  if (!verifier) throw new Error('Missing PKCE verifier');
  if (!storedState || storedState !== state) throw new Error('OAuth state mismatch');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: verifier,
  });

  const res = await fetch(`${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) {
    throw new Error(`Token exchange failed: ${res.status}`);
  }
  const json = (await res.json()) as {
    id_token: string;
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  };
  return {
    idToken: json.id_token,
    accessToken: json.access_token,
    refreshToken: json.refresh_token ?? null,
    expiresIn: json.expires_in,
  };
}

/**
 * Sign the user out by redirecting to Cognito's `/logout` endpoint. The
 * caller is responsible for clearing in-memory tokens before invoking.
 */
export function logoutRedirect(): void {
  const { domain, clientId } = readConfig();
  const returnTo = window.location.origin;
  const url = new URL(`${domain}/logout`);
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('logout_uri', returnTo);
  window.location.assign(url.toString());
}
